import {Injectable} from "@angular/core";
import {EntityData, LocalConfigurationService} from "@fedelta-pos/data-client";
import {NotificationService} from "../notification/notification.service";
import {TranslateService} from "ng2-translate";
import {ParamConfig, ParamConfigService} from "../config-support/param-config.service";
import {Local} from "../config-support/config";
import {LoadingStatus} from "../common-types/loading-status";
import {OrchestrationEvents, SessionReset} from "./orchestration-events";
import {NavigationService} from "./navigation.service";
import {ResponsiveService} from "./responsive.service";
import {OrderingPhase, SessionEvents} from "./session-events";
import {AppData, AppEntityModel, AppLoginOptionsData, TableLookupMethod} from "./app-entity.model";
import {AppEntryNotificationService} from "./app-entry-notification.service";
import {AppSessionService} from "./app-session.service";

@Injectable()
export class SessionModel {
    private static readonly SESSION_STARTED_MINIMUM_DELAY: number = 3500;

    /**
     * If populated then the App is in preview mode. The token is sent with the appId when retrieving the App to get preview data
     */
    public previewToken: string;

    private _currentApp: AppData;
    private _appSessionId: string;
    private _scanId: string;
    private _beaconId: string;
    private _beaconScanTime: number;
    private _beaconTableNumber: string;

    private _showExitButton: boolean;
    private _devOrDemoMode: boolean;
    private _nativeMode: boolean;
    private _waiterMode: boolean;
    private _noAutoScroll: boolean;
    private _defaultDateTime: Date;

    private testModeAcknowledged: boolean = false;

    private sessionStarted: number = Date.now();

    constructor(private appModel: AppEntityModel,
                private navigationService: NavigationService,
                private notificationService: NotificationService,
                private localConfigurationService: LocalConfigurationService,
                private responsiveService: ResponsiveService,
                private translate: TranslateService,
                private paramConfigService: ParamConfigService,
                private appSessionService: AppSessionService,
                private appEntryNotificationService: AppEntryNotificationService) {
        console.log("--- SessionModel");
        console.log("started=" + this.sessionStarted);

        // noinspection JSIgnoredPromiseFromCall
        this.initConfig();
    }

    private async initConfig(): Promise<void> {
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();

        this._appSessionId = this.appSessionService.getAppSessionId(paramConfig.scanId);
        if (paramConfig.unknownBeacon) {
            setTimeout(() => this.notificationService.displayAlert("woa.message.unrecognisedBeacon.title", "woa.message.unrecognisedBeacon.subTitle"));
        } else {
            this._scanId = paramConfig.scanId;
            this._beaconId = paramConfig.beaconId;
            this._beaconScanTime = paramConfig.beaconScanTime;
            this._beaconTableNumber = paramConfig.tableNumber;
        }
        this._nativeMode = paramConfig.nativeMode;
        this._waiterMode = paramConfig.waiterMode;
        this._noAutoScroll = paramConfig.noAutoScroll;
        this._showExitButton = paramConfig.showExitButton;
        const url: string = await this.localConfigurationService.getString(Local.DATASERVER_URL);
        const demo: boolean = await this.localConfigurationService.getBoolean(Local.DEMO);
        this._devOrDemoMode = !url || url.startsWith("http://localhost") || demo;
        this._defaultDateTime = paramConfig.defaultLocalDateTime ? EntityData.parseLocalDateTime(paramConfig.defaultLocalDateTime) : undefined;
    }

    public set orderingPhase(orderingPhase: OrderingPhase) {
        if (SessionEvents.orderingPhase$.value !== orderingPhase) {
            SessionEvents.orderingPhase$.next(orderingPhase);
        }
    }

    get currentApp(): AppData {
        return this._currentApp;
    }

    get showExitButton(): boolean {
        return this._showExitButton;
    }

    public get appSessionId(): string {
        return this._appSessionId;
    }

    public get scanId(): string {
        return this._scanId;
    }

    public get beaconId(): string {
        return this._beaconId;
    }

    public get beaconScanTime(): number {
        return this._beaconScanTime;
    }

    public get devOrDemoMode(): boolean {
        return this._devOrDemoMode;
    }

    public get timeTestMode(): boolean {
        return !!this._defaultDateTime;
    }

    public get now(): Date {
        return this._defaultDateTime || new Date();
    }

    public get nativeMode(): boolean {
        return this._nativeMode;
    }

    public get waiterMode(): boolean {
        return this._waiterMode;
    }

    public get noAutoScroll(): boolean {
        return this._noAutoScroll;
    }

    public get loginOptions(): AppLoginOptionsData {
        return this.responsiveService.isApp ? this._currentApp.appLoginOptions : this._currentApp.browserLoginOptions;
    }

    public get canSendOrderReceivedEmail(): boolean {
        return this._currentApp.sendOrderReceivedEmail && !!this._currentApp.orderReceivedEmail.id;
    }

    public get noTableMessage(): string {
        return this.effectiveTableLookupMethod === TableLookupMethod.QR_CODE ? "woa.error.order.scanTable" : "woa.error.order.enterTable";
    }

    public get effectiveTableLookupMethod(): TableLookupMethod {
        return this._beaconId && !this._beaconTableNumber ? TableLookupMethod.MANUAL
            : this.waiterMode ? TableLookupMethod.QR_CODE
                : (this._currentApp.tableLookupMethod || TableLookupMethod.MANUAL);
    }

    public async checkLiveMode(): Promise<void> {
        if (!this._currentApp.appLiveMode && !this.testModeAcknowledged) {
            this.testModeAcknowledged = await this.notificationService.displayAlertWithCheckbox(
                "woa.message.payment.appNotLive.title", "woa.message.payment.appNotLive.subTitle", "woa.message.payment.appNotLive.acknowledge");
        }
    }

    /**
     * If a phone number is available, and either uses the expected country code, or has no country code but looks like a mobile number, we assume it is valid for sending an SMS.
     */
    public isValidMobileNumberForSms(mobilePhone: string): boolean {
        return (!mobilePhone || !mobilePhone.match("[+]?\\d{10,30}")) ? false
            : !mobilePhone.startsWith("+") ? (mobilePhone.startsWith("04") && mobilePhone.length == 10)
                : this._currentApp.expectedCountryCode ? (mobilePhone.startsWith("+" + this._currentApp.expectedCountryCode + "4") && mobilePhone.length == (this._currentApp.expectedCountryCode.length + 10))
                    : (mobilePhone.charAt(mobilePhone.length - 9) == "4");
    }

    public async loadApp(force: boolean = false): Promise<void> {
        const firstLoad: boolean = !this._currentApp;
        if (!firstLoad && !force) {
            return;
        }

        const appId: number = await this.localConfigurationService.getNumber(Local.APP_ID);
        await this.appModel.loadEntity(this.previewToken ? appId + "_" + this.previewToken : appId);
        this._currentApp = this.appModel.data;
        SessionEvents.currentApp$.next(this.appModel.data);

        if (firstLoad) {
            OrchestrationEvents.firstAppLoad$.next(this.appModel.data);
        }
    }

    public clear(): void {
        this._currentApp = null;
        this.reset();
    }

    public reset(retainStore: boolean = false, retainOrderType: boolean = false): void {
        OrchestrationEvents.sessionReset$.next(new SessionReset(retainStore, retainOrderType));
    }

    public async notifyAppEntry(): Promise<void> {
        if (this.previewToken) {
            return;
        }

        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        return this.appEntryNotificationService.performAction({
            appSessionId: this._appSessionId,
            scanId: this._scanId,
            beaconId: this._beaconId,
            appId: this._currentApp ? this._currentApp.id : undefined,
            appName: this._currentApp ? this._currentApp.appName : undefined,
            liveMode: this._currentApp ? this._currentApp.appLiveMode : undefined,
            tableNumber: paramConfig.tableNumber,
            beaconOrderType: (paramConfig.tableOnlyMode && paramConfig.pickupOnlyMode) ? "TABLE_OR_PICKUP_ONLY"
                : paramConfig.tableOnlyMode ? "TABLE_ONLY"
                    : paramConfig.pickupOnlyMode ? "PICKUP_ONLY"
                        : undefined,
            storeId: paramConfig.exclusiveStore ? paramConfig.exclusiveStore
                : paramConfig.store ? paramConfig.store
                    : undefined,
            storeGroupId: paramConfig.exclusiveStoreGroup
        });
    }

    public notifyInitialLoadStatus(status: LoadingStatus) {
        setTimeout(() => {
            OrchestrationEvents.initialLoadStatus$.next(status);
            if (status === LoadingStatus.LOADED) {
                document.body.classList.add("initialLoadComplete");
            }
        }, SessionModel.SESSION_STARTED_MINIMUM_DELAY - (Date.now() - this.sessionStarted));
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/session.model.ts